import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, exhaustMap, tap, concatMap, toArray } from 'rxjs/operators';
import { from, of } from 'rxjs';

import * as BasketRaffleActions from './basketRaffle.actions';
import { BasketRaffleService } from '../services/basket-raffle.service';
import { ToastService } from 'app/toast/services/toast.service';
import { LoadingService } from 'app/core/services/loading.service';

@Injectable()
export class BasketRaffleEffects {

  loadBasketRaffleItemList$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(BasketRaffleActions.loadBasketRaffleList),
      exhaustMap((action: any) => {
        return this.basketRaffleService.getBasketRaffleItemList(action.payload).pipe(
          map(data => BasketRaffleActions.loadBasketRaffleListSuccess({ data })),
          catchError(error => of(BasketRaffleActions.loadBasketRaffleListFailure({ error }))))
      })
    );
  });

  createBasketRaffleItem$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(BasketRaffleActions.createBasketRaffleItem),
      exhaustMap(({ payload: { campaignId, basketItem }}: any) => {
        return this.basketRaffleService.createBasketRaffleItem(campaignId, basketItem).pipe(
          map((data) => BasketRaffleActions.createBasketRaffleItemSuccess({ data })),
          catchError(error => of(BasketRaffleActions.createBasketRaffleItemFailure({ error }))))
      })
    );
  });

  updateBasketRaffleItem$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(BasketRaffleActions.updateBasketRaffleItem),
      exhaustMap(({ payload: { campaignId, basketItem }}: any) => {
        return this.basketRaffleService.updateBasketRaffleItem(campaignId, basketItem).pipe(
          map(() => BasketRaffleActions.updateBasketRaffleItemSuccess({ campaignId, basketItem })),
          catchError(error => of(BasketRaffleActions.updateBasketRaffleItemFailure({ error }))))
      })
    );
  });

  updateBasketRaffleItemList$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(BasketRaffleActions.updateBasketRaffleList),
      exhaustMap(({ payload: { campaignId, basketItems }}: any) => {
        return this.basketRaffleService.updateBasketRaffleItemList(campaignId, basketItems).pipe(
          map(() => BasketRaffleActions.updateBasketRaffleListSuccess({ campaignId, basketItems })),
          catchError(error => of(BasketRaffleActions.updateBasketRaffleListFailure({ error }))))
      })
    );
  });
  
  deleteBasketRaffleItems$ = createEffect(() => {
    return this.actions$.pipe( 
      ofType(BasketRaffleActions.deleteBasketRaffleItems),
      exhaustMap(({ payload: { campaignId, basketItemIds, hideLoading }}: any) => {
        if (hideLoading) {
          this.loadingService.enabled = false;
        }
        // delete items in sequence
        return from(basketItemIds).pipe( 
          concatMap(basketItemId => 
            this.basketRaffleService.deleteBasketRaffleItem(campaignId, basketItemId).pipe(
              catchError(error => of(null))
            )
          ),
          toArray(),
          tap(() => {
            this.loadingService.enabled = true;
          }),
          map(() => BasketRaffleActions.deleteBasketRaffleItemsSuccess({ basketItemIds })),
          catchError(error => {
            this.loadingService.enabled = true;
            this.toastService.show('error', 'Deletion failed');
            return of(BasketRaffleActions.deleteBasketRaffleItemsFailure({ error }));
          })
        )
      })
    );
  });

  constructor(
    private actions$: Actions,
    private basketRaffleService: BasketRaffleService,
    private toastService: ToastService,
    private loadingService: LoadingService
  ) {}

}
